/* Closing a leveraged position by hand.
 *
 * There was no way to close one at all: a contract entered into the book stayed
 * there. Two things this insists on.
 *
 * The figure asked for is what the exchange actually gave you, not what the
 * screen last showed. The unrealised number is offered as a starting point
 * because it is usually close, and it is offered in a field you are expected to
 * overwrite — a fill happens at a price, and the mark is only where the app had
 * got to.
 *
 * And the row is kept rather than deleted. What a leveraged position finally did
 * is the part worth keeping; deleting it would take the trade out of the record
 * along with the line on the screen. */

import { useState } from 'react';
import { moneySigned } from '../lib/format';
import { decimal } from '../lib/number';
import { useStore } from '../state/store';
import type { PerpView } from '../state/derive';
import { lastCloseFromTotal } from '../state/closePerp';

/* Open and close are the caller's: three things can happen to a contract and
   only one panel may be showing at a time, so the card owns which. */
export function ClosePerp({ perp, say, open, onOpen, onDone }: {
  perp: PerpView;
  say: (m: string) => void;
  open: boolean;
  onOpen: () => void;
  onDone: () => void;
}) {
  const { closePerp, state } = useStore();
  /* Whether MEXC has already settled this one. The margin and the result went
     back there when it closed, and the balance in this book was read from there
     afterwards — so the money is already in the figure on screen and must not
     be added to it again. Written by the account sync; see `mexcGone`. */
  const settledAtExchange = (state.settings.mexcGone ?? []).includes(perp.symbol);
  const [percent, setPercent] = useState(100);
  const [result, setResult] = useState(perp.unrealisedUsd.toFixed(2));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  /* Closing the rest, on the whole position's figure. See `lastCloseFromTotal`. */
  const [whole, setWhole] = useState(false);
  const before = perp.realizedUsdt ?? 0;
  const exchangeSoFar = typeof perp.exchangeRealisedUsdt === 'number' ? perp.exchangeRealisedUsdt : null;
  const [total, setTotal] = useState(((exchangeSoFar ?? before) + perp.unrealisedUsd).toFixed(2));

  const portion = percent / 100;
  /* A result can be negative — a loss is a result — so this is the one figure
     that is not checked for being above zero. It is read with `decimal`, which
     takes the comma a Polish keyboard produces, and an empty field now reads as
     nothing rather than as a close at exactly zero. */
  const value = decimal(result);
  const onWhole = whole && percent >= 100;
  // What this close records: typed directly, or the whole position less what is already recorded.
  const thisClose = onWhole ? lastCloseFromTotal(perp, decimal(total)) : value;
  const ready = Number.isFinite(thisClose) && percent > 0;
  const releasedMargin = perp.marginUsdt * portion;

  /* Moving the slider re-suggests the figure, because closing a third of a
     position realises a third of what is standing on it — until you type your
     own, at which point the suggestion stops overwriting you. */
  const setShare = (next: number) => {
    setPercent(next);
    if (!touched) setResult(((perp.unrealisedUsd * next) / 100).toFixed(2));
  };

  const close = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      await closePerp(perp.id, thisClose, portion, { walletAlreadySettled: settledAtExchange });
      say(settledAtExchange
        ? `${perp.display} recorded as closed at ${moneySigned(thisClose, 'USD')}. `
          + 'The balance was left alone: MEXC had already put the money back and this book had '
          + 'already read it.'
        : percent >= 100
        ? `${perp.display} closed at ${moneySigned(thisClose, 'USD')}. `
          + `${(releasedMargin + thisClose).toFixed(2)} went back to the wallet.`
        : `${percent}% of ${perp.display} closed at ${moneySigned(thisClose, 'USD')}. `
          + `${(releasedMargin + thisClose).toFixed(2)} back to the wallet, `
          + `${(perp.marginUsdt - releasedMargin).toFixed(2)} still posted.`);
      onDone();
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    /* Tinted by where the position stands, not by what the button does — it
       echoes the unrealised figure sitting a line above it, which carries the
       same state in words and an arrow (§11). Pushed right so it reads as the
       action on the card rather than another line of it. */
    const up = perp.unrealisedUsd >= 0;
    return (
      <button
        className={`btn btn-sm ${up ? 'btn-up' : 'btn-down'}`}
        style={{ marginLeft: 'auto' }}
        onClick={onOpen}
      >
        Close it
      </button>
    );
  }

  return (
    <div style={{ width: '100%', marginTop: 10 }}>
      <div className="field">
        <label htmlFor={`share-${perp.id}`}>How much of it · {percent}%</label>
        <input
          id={`share-${perp.id}`} type="range" min={1} max={100} step={1}
          value={percent} onChange={(e) => setShare(Number(e.target.value))}
          style={{ width: '100%' }}
        />
        <div className="seg" style={{ marginTop: 8 }}>
          {[25, 50, 75, 100].map((q) => (
            <button key={q} aria-pressed={percent === q} onClick={() => setShare(q)}>
              {q === 100 ? 'All' : `${q}%`}
            </button>
          ))}
        </div>
      </div>

      {/* Closing the last of it: the exchange shows the whole position's result,
          funding and fees included, and not what this last part came to. So that
          figure can be given instead, and this close is worked out from it. */}
      {percent >= 100 ? (
        <div className="seg" style={{ marginBottom: 10 }}>
          <button aria-pressed={!whole} onClick={() => setWhole(false)}>This close</button>
          <button aria-pressed={whole} onClick={() => setWhole(true)}>Whole position</button>
        </div>
      ) : null}

      {onWhole ? (
        <div className="field">
          <label htmlFor={`total-${perp.id}`}>What the whole position came to, in USDT</label>
          <input
            id={`total-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
            value={total} onChange={(e) => setTotal(e.target.value)}
          />
          <div className="hint">
            The closed position&rsquo;s result on MEXC, with funding and fees already in it.
            {exchangeSoFar !== null
              ? ` Filled in with MEXC's realised figure at the last read plus what is open now.`
              : ' Filled in with what is recorded so far plus what is open now.'}
          </div>
          <div className="kv" style={{ marginTop: 8 }}>
            <span className="k">Recorded in earlier closes</span>
            <span className="v num">{moneySigned(before, 'USD')}</span>
          </div>
          <div className="kv">
            <span className="k">So this close records</span>
            <span className={`v num ${Number.isFinite(thisClose) && thisClose < 0 ? 'down' : ''}`}>
              {Number.isFinite(thisClose) ? moneySigned(thisClose, 'USD') : '—'}
            </span>
          </div>
        </div>
      ) : (
        <div className="field">
          <label htmlFor={`close-${perp.id}`}>What it came to, in USDT</label>
          <input
            id={`close-${perp.id}`} className="input" data-mono="true" inputMode="decimal"
            value={result} onChange={(e) => { setTouched(true); setResult(e.target.value); }}
          />
          <div className="hint">
            Filled in with what the app last marked it at. Replace it with the figure on the
            exchange, which is the one that happened.
          </div>
        </div>
      )}

      <div className="kv">
        <span className="k">Back to the wallet</span>
        <span className="v num">
          {settledAtExchange
            ? 'already there'
            : `${ready ? (releasedMargin + thisClose).toFixed(2) : '—'} USDT`}
        </span>
      </div>
      {percent < 100 ? (
        <div className="kv">
          <span className="k">Still posted</span>
          <span className="v num">{(perp.marginUsdt - releasedMargin).toFixed(2)} USDT</span>
        </div>
      ) : null}
      {settledAtExchange ? (
        <div className="note" style={{ marginTop: 8 }}>
          MEXC stopped reporting this contract, so the margin and the result are already in the
          free balance this book read from there. Recording it here writes the trade down and
          leaves the balance where it is, because adding it again would count the same money
          twice.
        </div>
      ) : null}

      <div className="dim" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.45 }}>
        {percent >= 100
          ? `Margin of ${perp.marginUsdt.toFixed(2)} plus the result. The contract stays in the
             book, marked closed, so the trade is still on the record.`
          : `Margin and notional come down by ${percent}%. What you paid to get in does not change
             because some of it was sold, and the liquidation price is left as the exchange has
             it. Recomputing that would be inventing the number that decides when you are
             liquidated.`}
      </div>

      <div className="fab-row" style={{ marginTop: 10 }}>
        <button className="btn btn-block" onClick={onDone}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!ready || busy}
                onClick={() => void close()}>
          {busy ? 'Closing…' : percent >= 100 ? 'Record the close' : `Close ${percent}%`}
        </button>
      </div>
    </div>
  );
}
